import { useAuth, useCachedFetch } from '@structural-climbing/auth'
import { useMyGyms } from '../gym/useMyGyms'
import type { CompetitionSummary } from './types'

// Normalise at the boundary. The nav asks for this list on every screen now, so
// a body that is not an array has to become an empty list rather than blow up
// inside the spread below.
function asList(body: unknown): CompetitionSummary[] {
  return Array.isArray(body) ? (body as CompetitionSummary[]) : []
}

/**
 * The cache key for a competition list.
 *
 * It has to name the gyms, because that is what the list depends on: keying it
 * "all" meant the empty list a member loaded before being approved stayed valid
 * afterwards, and their new gym's competitions never appeared without a hard
 * refresh. Sorted so the same set always produces the same key.
 *
 * The scope is in the key too, and has to be: a member of exactly one gym
 * produces the same gym-id list either way, but the two scopes fetch different
 * things — one gym's competitions, versus those merged with the open ones
 * everywhere else. Sharing an entry between them let a competition at a gym the
 * member does not belong to reach the dashboard teaser, which links it under
 * the member's own gym slug.
 */
export function competitionsCacheKey(gymIds: string[], scope: 'gym' | 'all'): string {
  return `competitions:${scope}:${[...gymIds].sort().join(',') || 'none'}`
}

/**
 * How many competitions are still to come or running right now.
 *
 * What the Climb tab's dot counts. `state` is resolved server-side against each
 * host gym's own clock, so "is it over" needs no date maths here.
 */
export function liveCompetitionCount(competitions: CompetitionSummary[]): number {
  return competitions.filter((c) => c.state !== 'ended').length
}

/**
 * Published competitions the member can see.
 *
 * With a gymId it is that gym's, which is what the dashboard teaser wants.
 * Without one it spans every gym they belong to, which is what the competitions
 * page needs now that it no longer lives under a gym slug.
 */
export function useCompetitions(
  gymId: string | null | undefined,
  apiUrl: string,
  getAccessTokenSilently: () => Promise<string>,
) {
  const { isAuthenticated } = useAuth()
  const { gyms, loading: gymsLoading } = useMyGyms()
  const gymIds = gymId ? [gymId] : gyms.map((g) => g.id)

  // Nothing to ask for until we know which gyms to ask about — and nothing to
  // ask with until there is a session. The nav mounts this hook from the shell,
  // which renders on /login too, and a logged-out member resolves to an empty
  // gym list rather than a null key, so without this guard the login screen
  // fires a silent-auth attempt that cannot succeed.
  const knownGyms = gymId || !gymsLoading
  const key =
    isAuthenticated && knownGyms ? competitionsCacheKey(gymIds, gymId ? 'gym' : 'all') : null

  const { data, loading } = useCachedFetch<CompetitionSummary[]>(key, async () => {
    const token = await getAccessTokenSilently()

    if (gymId) {
      const res = await fetch(`${apiUrl}/my-competitions?gym_id=${encodeURIComponent(gymId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to load competitions')
      return asList(await res.json())
    }

    // Two sources: the member's own gyms, which includes their members-only
    // competitions and says which they have entered, and the open ones anywhere
    // else. Without the second, someone who belongs to no gym sees an empty
    // page even though there are competitions they could enter.
    const [mineRes, openRes] = await Promise.all([
      fetch(`${apiUrl}/my-competitions`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`${apiUrl}/public-competitions`),
    ])
    if (!mineRes.ok) throw new Error('Failed to load competitions')

    const mine = asList(await mineRes.json())
    const seen = new Set(mine.map((c) => c.id))
    const open = openRes.ok
      ? asList(await openRes.json())
          // A competition at one of their gyms is already in `mine`, with the
          // joined flag the public list cannot know about.
          .filter((c) => !seen.has(c.id) && c.join_policy === 'open')
          .map((c) => ({ ...c, joined: false }))
      : []

    return [...mine, ...open]
  })

  return { competitions: data ?? [], loading: loading || (!gymId && gymsLoading) }
}
